import * as React from "react";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";
import Form from "react-bootstrap/Form";
import TimeAgo from 'timeago-react';
import { DatagridHelper } from "./DatagridHelper";
import { DatagridColumn, PageState, ColumnIds} from "../utils/types";
import StateHelper from "../utils/StateHelper";
import Pivot from "./Pivot";
import dayjs from "../utils/dayjs";
import { PagesHelper } from "../common/PagesHelper";

interface ShowHideColumnsProps {
  filteredColumns: DatagridColumn[];
  page: string;
  setRefresh:  (refresh:number) => void;
  show: boolean;
  setModalShow: (show:boolean) => void;
  invisColumnsArray: string[];
  setInvisColumnsArray: (newValues: string[]) => void
}

const KEY_INVIS_COLS = "invisCols";

export const Helper = {

  getInvisibleColumnsLocalStorage: function( page : string ): string[] {
    let invisCols: string = window.localStorage.getItem(KEY_INVIS_COLS + page);
  
    let invisColumnsArray: string[] = [];
    if (invisCols) {
      invisColumnsArray = invisCols.split(",");
    }
    return invisColumnsArray;
  },

  setInvisibleColumnsLocalStorage(page : string, value : string){
    StateHelper.setLocalStorageItem(KEY_INVIS_COLS + page,value);
  },

  setVisible : function(columnId : string, value : boolean , page : string, setInvisColumnsArray: (newValues: string[]) => void) {
    const invisColumnsArray = this.getInvisibleColumnsLocalStorage( page );
    if (value === false) {
      //make it hidden, add to localstorage list 

      //check if already there before adding.
      if (invisColumnsArray.includes(columnId) === true) return;

      invisColumnsArray.push(columnId);
      this.setInvisibleColumnsLocalStorage(page, invisColumnsArray.join(","));
      setInvisColumnsArray(invisColumnsArray);
    } else {
      //remove the columnid from the list
      const filtered = invisColumnsArray.filter((value) => value !== columnId);

      this.setInvisibleColumnsLocalStorage(page, filtered.join(","));
      setInvisColumnsArray(filtered);
    }
  }
};

function ShowHideColumns(props: ShowHideColumnsProps) {
  let checkboxesArray: any = [];
  const sortedColumns = [...props.filteredColumns].sort((a, b) => a.name.localeCompare(b.name));
  for (const column of sortedColumns) {
    if( column.isGroupByColumn === false){
      let isInvisColumn = props.invisColumnsArray.includes(column.id);

      let keyChk : string = "chk" + column.id;
      let keyLi: string = "chkli" + column.id;
      let obj = (
        <ListGroup.Item
          key={keyChk}
          onClick={() => onChange(isInvisColumn, column.id, props.setInvisColumnsArray)}
        >
          <Form.Check
            key={keyLi}
            type="checkbox"
            style={{ fontSize: 12 }}
            checked={!isInvisColumn}
            label={column.name}
          />
        </ListGroup.Item>
      );
      checkboxesArray.push(obj);
    }
  }

  function onChange(checked: boolean, id: string, setInvisColumnsArray: (newValues: string[]) => void) {
    Helper.setVisible(id, checked, props.page, setInvisColumnsArray);
    props.setRefresh(Date.now());
  }

  function onHide() {
    props.setModalShow(false);
  }

  return (
    <Modal
      {...props}

      onHide={onHide}
      size="sm"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>Show/Hide Columns</Modal.Header>
      <Modal.Body style={{ maxHeight: "calc(95vh - 200px)", overflowY: "auto" }}>
        <ListGroup>{checkboxesArray}</ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export function OtherFeaturesPanel(props: any) {
  const [modalShow, setModalShow] = React.useState(false);
  const [invisColumnsArray, setInvisColumnsArray] = React.useState<string[]>(Helper.getInvisibleColumnsLocalStorage(props.page));

  function exportOnClick(e:React.SyntheticEvent<any>) {

    const visibleColumns : DatagridColumn[] =  props.filteredColumns.filter( ( (r : any) => r.visible));

    // Build column headers - add "Demand Source Name" after "Campaign Source" if it exists
    const columnHeaders: string[] = [];
    visibleColumns.forEach((col) => {
      // Override Campaign Source column name to "Demand Source ID" in export
      const exportColumnName = col.id === ColumnIds.campaignSource ? "Demand Source ID" : col.name;
      columnHeaders.push('"' + exportColumnName + '"');
      // Add "Demand Source Name" column after "Campaign Source"
      if (col.id === ColumnIds.campaignSource) {
        columnHeaders.push('"Demand Source Name"');
      }
    });
    const columnNamesRow : string = columnHeaders.join(",") + "\n";

    // Build data rows - add demand source name after campaign source ID
    let csvData = "";
    props.rows.map( (row : any) => {
      const rowData: string[] = [];
      visibleColumns.forEach((col : DatagridColumn) => {
        // Add the regular column value
        rowData.push('"' + DatagridHelper.formatValueForExport( col ,row[col.id] ) + '"');

        // If this is the campaign source column, also add the demand source name
        if (col.id === ColumnIds.campaignSource && row[col.id]) {
          const demandSourceName = PagesHelper.getDemandSourceLabel(row[col.id].toString(), false);
          rowData.push('"' + demandSourceName + '"');
        } else if (col.id === ColumnIds.campaignSource && !row[col.id]) {
          // If campaign source is empty, add empty cell for demand source name
          rowData.push('""');
        }
      });
      return csvData += rowData.join(",") + "\n";
    });

    const csvContent : string = "data:text/csv;charset=utf-8," + columnNamesRow + csvData;
    const encodedUri : string = encodeURI(csvContent);

    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);

    const pageState : PageState = StateHelper.getPageState();

    const strDateStart = pageState.dateStart.toISOString().split('T')[0];
    const strDateEnd = pageState.dateEnd.toISOString().split('T')[0];
    let pivotBy = pageState.pivotBy ? pageState.pivotBy.split(":")[0] : "";
    if (pivotBy) {
      // _pivot_by_ + camel case to snake case convertion
      pivotBy = "_pivot_by_" + pivotBy.replace(/(.)([A-Z][a-z]+)/, '$1_$2').replace(/([a-z0-9])([A-Z])/, '$1_$2').toLowerCase();
    }

    const fileName = strDateStart + "_" + strDateEnd + pivotBy +  "_kepler_" + props.page + "_export.csv";

    link.setAttribute("download", fileName);
    document.body.appendChild(link); // Required for FF

    link.click(); // This will download the data file named "my_data.csv".
  }

  const dataLastUpdate = dayjs.utc(props.dataLastUpdate);
  const queryTimestamp = dayjs.utc(props.queryTimestamp);
  const pageState : PageState = StateHelper.getPageState();

  return (
    <Row>
      <Col sm={8} className="text-left">
        <div style={{ fontSize: 12  }}>Reporting data updated: <TimeAgo title={"Reporting data updated: " + dataLastUpdate.local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"}  datetime={dataLastUpdate.toDate()}  locale='en_US'/></div>
        <div style={{ fontSize: 12  }}>Last queried: <TimeAgo title={"Last queried: " + queryTimestamp.local().format("YYYY-MM-DD hh:mm:ss") + " (local time)"} datetime={queryTimestamp.toDate()}  locale='en_US'/></div>
        <div style={{ fontSize: 12  }}>Results for: {pageState.dateStart && pageState.dateStart.toISOString().split('T')[0]} to {pageState.dateEnd && pageState.dateEnd.toISOString().split('T')[0]}</div>
      </Col>
      <Col sm={2} className="text-center">
        <Pivot {...props} />
      </Col>

      <Col sm={1} className="text-center">
        <Button 
          style={{ fontSize: 12  }}
          size="sm"
          variant="outline-primary"
          onClick={exportOnClick}
          >
        Export as CSV  
        </Button>
      </Col>
      <Col sm={1} className="text-center">
          {" "}
          <Button
            style={{ fontSize: 12  }}
            size="sm"
            variant="outline-primary"
            disabled={!props.filteredColumns.filter((c:any) => c.visible && !c.isGroupByColumn).length}
            onClick={() => setModalShow(true)}
          >Visible Columns</Button>
          <ShowHideColumns
            page={props.page}
            setRefresh={props.setRefresh}
            filteredColumns={props.filteredColumns}
            show={modalShow}
            setModalShow={setModalShow}
            invisColumnsArray={invisColumnsArray}
            setInvisColumnsArray={setInvisColumnsArray}
          />
      </Col>
    </Row>
  );
}
